import * as React from 'react'
import { createPortal } from 'react-dom'
import { cn } from '@/lib/utils'

/**
 * A simple bottom drawer that's always visible at the bottom of the screen.
 * Can be resized by dragging the top handle area up/down to change height.
 * Accepts an optional header prop that renders in the draggable area.
 */
export function SwipeableDrawer({
  className,
  children,
  minHeight = 100,
  header,
  ...props
}) {
  const drawerRef = React.useRef(null)
  const handleRef = React.useRef(null)
  const headerRef = React.useRef(null)
  const [isDragging, setIsDragging] = React.useState(false)
  const [drawerHeight, setDrawerHeight] = React.useState(minHeight)
  const [headerHeight, setHeaderHeight] = React.useState(0)
  const startYRef = React.useRef(0)
  const startHeightRef = React.useRef(minHeight)

  // Measure header height
  React.useEffect(() => {
    if (headerRef.current) {
      const height = headerRef.current.offsetHeight
      setHeaderHeight(height)
    }
  }, [header])

  const handleTouchStart = React.useCallback((e) => {
    setIsDragging(true)
    startYRef.current = e.touches[0].clientY
    startHeightRef.current = drawerHeight

    // Remove transition during drag for smooth following
    if (drawerRef.current) {
      drawerRef.current.style.transition = 'none'
    }
  }, [drawerHeight])

  const handleTouchMove = React.useCallback((e) => {
    if (!isDragging) return

    const currentY = e.touches[0].clientY
    const deltaY = startYRef.current - currentY // Inverted: dragging up increases height
    const newHeight = Math.max(minHeight, Math.min(window.innerHeight * 0.9, startHeightRef.current + deltaY))

    setDrawerHeight(newHeight)

    e.preventDefault()
  }, [isDragging, minHeight])

  const handleTouchEnd = React.useCallback(() => {
    setIsDragging(false)

    // Add transition back for smooth settling
    if (drawerRef.current) {
      drawerRef.current.style.transition = 'height 200ms ease-out'
    }
  }, [])

  // Use manual event listeners to avoid passive event listener issues
  React.useEffect(() => {
    const handleElement = handleRef.current
    if (!handleElement) return

    const options = { passive: false }

    handleElement.addEventListener('touchstart', handleTouchStart, options)
    handleElement.addEventListener('touchmove', handleTouchMove, options)
    handleElement.addEventListener('touchend', handleTouchEnd, options)

    return () => {
      handleElement.removeEventListener('touchstart', handleTouchStart, options)
      handleElement.removeEventListener('touchmove', handleTouchMove, options)
      handleElement.removeEventListener('touchend', handleTouchEnd, options)
    }
  }, [handleTouchStart, handleTouchMove, handleTouchEnd])

  return createPortal(
    <div
      ref={drawerRef}
      className={cn(
        'fixed left-0 right-0 bottom-0 z-50 bg-white shadow-2xl outline-none',
        'w-full rounded-t-xl',
        className
      )}
      style={{
        height: `${drawerHeight}px`,
        willChange: 'height',
        boxShadow: '0 -4px 20px rgba(0, 0, 0, 0.15), 0 -2px 8px rgba(0, 0, 0, 0.1)'
      }}
      {...props}
    >
      {/* Drag handle area - taller and more clickable */}
      <div
        ref={handleRef}
        className="flex flex-col touch-none"
        style={{ minHeight: '48px' }}
      >
        {/* Handle bar */}
        <div className="flex items-center justify-center py-2">
          <div className="h-1.5 w-12 rounded-full bg-gray-400" />
        </div>
        {/* Header content */}
        {header && (
          <div ref={headerRef} className="px-4 pb-1">
            {header}
          </div>
        )}
      </div>
      <div
        className="overflow-auto"
        style={{ height: `calc(${drawerHeight}px - ${headerHeight}px)` }}
      >
        {children}
      </div>
    </div>,
    document.body
  )
}

SwipeableDrawer.displayName = 'SwipeableDrawer'
